import { Empty } from 'antd';

import SpaceSpinner from '../../../components/SpaceSpinner';
import FuturePeriodPlaceholder from './FuturePeriodPlaceholder';
import PipelineLegend from './PipelineLegend';
import StageCard, { PipelineArrow } from './StageCard';
import classNames from './styles.module.scss';
import usePipelineModel from './usePipelineModel';

const ReimbursementPipeline = ({
  t,
  timeline,
  timelineFilters,
  isTimelineLoading,
  onPeriodChange,
}) => {
  const {
    pipelineModel,
    isCurrentPeriod,
    isFuturePeriod,
    currentPeriodDates,
    hasPeriod,
    submissions,
    approvals,
    payments,
  } = usePipelineModel({ timeline, timelineFilters, isTimelineLoading, t });

  if (!hasPeriod) {
    return null;
  }

  if (isTimelineLoading || !timeline) {
    return <SpaceSpinner height={260} />;
  }

  if (!timeline.length) {
    if (isFuturePeriod) {
      const handleViewCurrentPeriod =
        onPeriodChange && currentPeriodDates
          ? () =>
              onPeriodChange({
                groupID: timelineFilters.groupID,
                startDate: currentPeriodDates.startDate,
                endDate: currentPeriodDates.endDate,
              })
          : undefined;
      return <FuturePeriodPlaceholder onViewCurrentPeriod={handleViewCurrentPeriod} />;
    }
    return (
      <Empty
        description={<p className={classNames.emptyHint}>{t('noDataAvailableOnSelectedPeriod')}</p>}
      />
    );
  }

  return (
    <div>
      <div className={classNames.pipelineHeader}>
        <h2 className={classNames.pipelineTitle}>{t('reimbursementPipeline')}</h2>
        {isCurrentPeriod !== null && pipelineModel && (
          <span
            className={[
              classNames.periodBadge,
              isCurrentPeriod ? classNames.periodBadgeCurrent : classNames.periodBadgePast,
            ].join(' ')}
          >
            {pipelineModel.periodPill}
          </span>
        )}
      </div>

      {pipelineModel && (
        <div className={classNames.pipeline}>
          <StageCard
            cardModifier={pipelineModel.s1.cardModifier}
            stageNum={1}
            stageName={t('tripSubmissions')}
            dateISO={submissions?.date}
            dateFieldLabel={t('deadline')}
            pillModifier={pipelineModel.s1.pillModifier}
            pillText={pipelineModel.s1.pillText}
            progressMetaLeft={pipelineModel.s1.metaL}
            progressMetaRight={pipelineModel.s1.metaR}
            progressFillClass={pipelineModel.s1.fill}
            progressWidthPct={pipelineModel.s1.w}
            footer={pipelineModel.s1.footer}
          />
          <PipelineArrow />
          <StageCard
            cardModifier={pipelineModel.s2.cardModifier}
            stageNum={2}
            stageName={t('approvals')}
            dateISO={approvals?.date}
            dateFieldLabel={t('deadline')}
            pillModifier={pipelineModel.s2.pillModifier}
            pillText={pipelineModel.s2.pillText}
            progressMetaLeft={pipelineModel.s2.metaL}
            progressMetaRight={pipelineModel.s2.metaR}
            progressFillClass={pipelineModel.s2.fill}
            progressWidthPct={pipelineModel.s2.w}
            footer={pipelineModel.s2.footer}
          />
          <PipelineArrow />
          <StageCard
            cardModifier={pipelineModel.s3.cardModifier}
            stageNum={3}
            stageName={t('reimbursementInitiated')}
            dateISO={payments?.date}
            dateFieldLabel={t('initiationDate')}
            pillModifier={pipelineModel.s3.pillModifier}
            pillText={pipelineModel.s3.pillText}
            progressMetaLeft={pipelineModel.s3.metaL}
            progressMetaRight={pipelineModel.s3.metaR}
            progressFillClass={pipelineModel.s3.fill}
            progressWidthPct={pipelineModel.s3.w}
            footer={pipelineModel.s3.footer}
          />
        </div>
      )}

      <PipelineLegend t={t} />
    </div>
  );
};

export default ReimbursementPipeline;
